import {
	Dialog,
	NoResults,
	Toolbar,
	renderReactDialog
} from "client/Components";
import {
	AlertManager,
	ExchangeAvailability,
	useAlerts,
	useExchangeAvailability,
	useOpenDialog,
	useUnmountedCancellation
} from "client/Hooks";
import { ReactElement, useState } from "react";
import { EmailNotConfiguredDialog } from "../Email/Dialogs/EmailNotConfiguredDialog";
import { Details } from "./Details/Details";
import { EditDialog } from "./Edit/EditDialog";
import { AddressBookContactSource } from "./Overview/Overview";
import {
	ContactDetailsModel,
	ContactKind,
	ContactSource,
	ExchangeContactDetailsModel,
	deletePersonalContact
} from "client/Api/Email/contacts";
import { isNil, isNotNil } from "client/Misc/utils";
import { DialogBody } from "client/Components/Dialog/DialogBody/DialogBody";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { useFetchContactDetails } from "./Details/useFetchContactDetails";
import { getComputedDisplayName } from "./common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { useDeleteContactConfirmationDialog } from "./DeleteContactConfirmationDialog";

export type ContactDialogView = {
	kind: "details";
	contactId: string;
} | {
	kind: "create";
};

export type ContactDialogProps = {
	source: AddressBookContactSource;
	initialView: ContactDialogView;
	onClose(shouldRefreshList: boolean): void;
};

export const ContactDialog = (props: ContactDialogProps): ReactElement => {
	const {
		source,
		initialView,
		onClose
	} = props;

	const exchangeAvailability: ExchangeAvailability = useExchangeAvailability();

	const [contactId, setContactId] = useState<string | undefined>(
		(): string | undefined => {
			return initialView.kind === "details"
				? initialView.contactId
				: undefined;
		}
	);

	const [editDialog, setEditDialog] = useState<EditDialogState>((): EditDialogState => {
		if (initialView.kind === "create") {
			return {
				isVisible: true
			};
		} else {
			return {
				isVisible: false
			};
		}
	});

	const [shouldRefreshListOnClose, setShouldRefreshListOnClose] = useState<boolean>(false);
	const [isDeletingContact, setIsDeletingContact] = useState<boolean>(false);

	const {
		state: contactLoadingState,
		reload
	} = useFetchContactDetails(
		source,
		contactId
	);

	const [deleteConfirmationDialog, openDeleteConfirmationDialog] = useDeleteContactConfirmationDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const closeEditDialogHandler = (
		contactId: string | undefined,
		shouldReloadData: boolean
	): void => {
		if (isNotNil(contactId)) {
			setContactId(contactId);
			setEditDialog({ isVisible: false });
			setShouldRefreshListOnClose(shouldReloadData);

			if (shouldReloadData) {
				reload();
			}
		} else {
			onClose(shouldReloadData);
		}
	};

	const ensurePersonalContact = <TResult,>(
		contact: ContactDetailsModel,
		callback: (personalContact: ExchangeContactDetailsModel) => TResult
	): TResult => {
		switch (source) {
			case ContactSource.Workspace:
				throw new Error("Only exchange contacts could be edited.");
			case ContactSource.Gal:
				throw new Error("Global contact cannot be edited.");
			case ContactSource.Personal:
				if (contact.kind === ContactKind.Workspace) {
					throw new Error("Only exchange contacts could be edited.");
				}

				return callback(contact);
			default:
				never(source);
		}
	};

	const editDetailsHandler = (contact: ContactDetailsModel): void => {
		ensurePersonalContact(
			contact,
			personalContact => setEditDialog({
				isVisible: true,
				contact: personalContact
			})
		);
	};

	const deletePersonalContactHandler = async (contact: ContactDetailsModel): Promise<void> => {
		await ensurePersonalContact(
			contact,
			async (personalContact: ExchangeContactDetailsModel): Promise<void> => {
				if (!await openDeleteConfirmationDialog({
					contactDisplayName: getComputedDisplayName(personalContact)
						.computedDisplayName,
					trackingId: `delete-contact-confirmation-dialog`
				})) {
					return;
				}

				try {
					setIsDeletingContact(true);
					await deletePersonalContact(personalContact.id, globalCancellation);
					onClose(true);
					alertManager.showSuccess(localize("_SuccessfullyDeletedTheContact"));
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}
					setIsDeletingContact(false);
					alertManager.showError(localize("_FailedToDeleteTheContact"));
					console.error(e);
				}
			}
		);
	};

	if (exchangeAvailability !== ExchangeAvailability.Available) {
		return (
			<EmailNotConfiguredDialog
				shouldNavigateBackToWorkspaceOnClose={false}
				hideConfigurationSuggestion={exchangeAvailability !== ExchangeAvailability.RequiresConfiguration}
				onClose={() => onClose(false)}
			/>
		);
	}

	if (editDialog.isVisible) {
		return (
			<EditDialog
				exchangeContactId={editDialog.contact?.id}
				onSave={(contactId) => closeEditDialogHandler(contactId, true)}
				onClose={() => closeEditDialogHandler(contactId, false)}
			/>
		);
	}

	if (isNil(contactLoadingState)) {
		throw new Error("ContactId is not set, cannot render contact's details.");
	}

	const renderContent = (): ReactElement | null => {
		switch (contactLoadingState.kind) {
			case "loading":
				return null;
			case "loaded":
				return (
					<Details
						contact={contactLoadingState.contact}
					/>
				);
			case "error":
				if (contactLoadingState.errorKind === "not-found"
					&& source === ContactSource.Workspace
				) {
					return (
						<NoResults
							icon="user"
							title={localize("_UserDoesNotExist")}
							message={localize("_TheUserIsDeletedOrInactive")}
							trackingId="loading-error"
							height="fill"
						/>
					);
				} else {
					return (
						<NoResults
							icon="gear"
							title={localize("_FailedToRetrieveTheContactDetails")}
							message={localize("_WeWereUnableToRetrieveContactDetails")}
							onRetry={reload}
							trackingId="loading-error"
							height="fill"
						/>
					);
				}
			default:
				never(contactLoadingState);
		}
	};

	return (
		<>
			<Dialog
				hasBusyIndicator={contactLoadingState.kind === "loading" || isDeletingContact}
				trackingId="contact-details-dialog"
				variant="left-slider"
				onClose={() => onClose(shouldRefreshListOnClose)}
			>
				<Dialog.Header
					title={localize(sourceToTitleLocalizationKey[source])}
					onClose={() => onClose(shouldRefreshListOnClose)}
					hasBorder
				/>
				<DialogBody>
					{renderContent()}
				</DialogBody>
				<Dialog.Footer hasBorder>
					<Toolbar>
						{source === ContactSource.Personal && contactLoadingState.kind === "loaded" && (
							<>
								<Toolbar.Button
									kind="secondary"
									text={localize("_Delete")}
									onClick={() => deletePersonalContactHandler(contactLoadingState.contact)}
								/>
								<Toolbar.Button
									kind="primary"
									text={localize("_Edit")}
									onClick={() => editDetailsHandler(contactLoadingState.contact)}
								/>
							</>
						)}
						<Toolbar.Button
							kind="secondary"
							text={localize("_Close")}
							onClick={() => onClose(shouldRefreshListOnClose)}
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{deleteConfirmationDialog}
		</>
	);
};
ContactDialog.displayName="ContactDialog";

export type ContactDialogData = PartlyPartial<ContactDialogProps, "onClose">;

export function showContactDialog(props: ContactDialogData): void {
	const {
		onClose,
		...restOfTheProps
	} = props;

	return renderReactDialog(closeCallback => {
		const closeHandler = (shouldRefreshList: boolean): void => {
			onClose?.(shouldRefreshList);
			closeCallback();
		};

		return <ContactDialog {...restOfTheProps} onClose={closeHandler} />;
	});
}

export function useContactDialog(): [
	element: ReactElement | null,
	open: (openData: ContactDialogData) => Promise<void>
] {
	return useOpenDialog<ContactDialogData>(
		(openData, onClose) => {
			const {
				onClose: onCloseFromOpen,
				...props
			} = openData;

			return (
				<ContactDialog
					{...props}
					onClose={(shouldRefreshList) => {
						onCloseFromOpen?.(shouldRefreshList);
						onClose();
					}}
				/>
			);
		}
	);
}

const sourceToTitleLocalizationKey: { [key in AddressBookContactSource]: LocalizationKey } = {
	[ContactSource.Personal]: "_PersonalContact",
	[ContactSource.Gal]: "_OrganisationContact",
	[ContactSource.Workspace]: "_WorkspaceContact",
};

type EditDialogState = {
	isVisible: true;
	contact?: ExchangeContactDetailsModel;
} | {
	isVisible: false;
};